import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Beaker, Sparkles, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const Home = () => {
  const brands = [
    {
      title: 'Molecular Hydrogen',
      subtitle: 'Axiom H2',
      description: 'Harness the power of molecular hydrogen (H₂) for cellular optimization. Advanced technology designed to support healthy oxidative balance and promote overall vitality.',
      icon: Beaker,
      image: 'https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/s34t1beg_image.png',
      link: '/molecular-hydrogen',
      color: 'from-[#0D2847] to-[#1a3a5f]'
    },
    {
      title: 'Skincare',
      subtitle: 'Beauté Pacifique',
      description: 'Danish cosmeceutical skincare with scientifically proven results. Premium products that effectively work on age degeneration and sun damage.',
      icon: Sparkles,
      image: 'https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/p9h3iuow_image.png',
      link: '/skincare',
      color: 'from-[#0D2847] to-[#1a3a5f]'
    },
    {
      title: 'Red Light / Infrared Therapy',
      subtitle: 'Therasage',
      description: 'Full spectrum infrared portable saunas and healing pads. Over 25 years of trusted red light and infrared technology for holistic wellness.',
      icon: Zap,
      image: 'https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/bix546un_image.png',
      link: '/infrared-therapy',
      color: 'from-[#0D2847] to-[#1a3a5f]'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-[700px] md:min-h-[800px] bg-cover bg-center flex items-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/1z9lpww2_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D2847]/40 via-[#0D2847]/30 to-[#0D2847]/50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white relative z-10 py-20">
          <div className="mb-6 animate-fade-in">
            <span className="inline-block bg-white/10 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium border border-white/20">
              Science-Backed Wellness Solutions
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title">
            Transform Your Health
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300 mt-2">
              At The Cellular Level
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-10 text-white/90 max-w-4xl mx-auto leading-relaxed">
            Experience the synergy of molecular hydrogen therapy, premium cosmeceutical skincare, 
            and infrared healing technology—all backed by rigorous scientific research.
          </p>

          {/* Key Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-4xl font-bold text-cyan-300 mb-2">25+</div>
              <div className="text-base text-white/90">Years of Innovation</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-4xl font-bold text-cyan-300 mb-2">100%</div>
              <div className="text-base text-white/90">Science-Backed Solutions</div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => document.getElementById('wellness-solutions').scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0D2847] hover:bg-white/90 px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all rounded-md h-auto"
            >
              Discover Our Solutions
              <ArrowRight className="ml-2" size={20} />
            </button>
            <Link to="/contact">
              <Button size="lg" className="bg-[#0D2847] text-white hover:bg-[#0D2847]/90 border-2 border-white px-8 py-6 text-lg font-semibold shadow-xl">
                Get Started Today
              </Button>
            </Link>
          </div>

          {/* Scroll indicator */}
          <div className="mt-16 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full mx-auto flex justify-center">
              <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Sections */}
      <section id="wellness-solutions" className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0D2847] mb-4">Our Wellness Solutions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our curated selection of premium wellness brands, each backed by scientific research and proven results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {brands.map((brand, index) => (
              <Link key={index} to={brand.link} className="no-underline">
                <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer h-full" data-testid={`brand-card-${index}`}>
                  <div className="relative h-64 overflow-hidden bg-white">
                    <img 
                      src={brand.image} 
                      alt={brand.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${brand.color}`}>
                        <brand.icon className="text-white" size={24} />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{brand.title}</CardTitle>
                        <p className="text-sm text-gray-500">{brand.subtitle}</p>
                      </div>
                    </div>
                    <CardDescription className="text-base">
                      {brand.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-[#0D2847] hover:bg-[#0D2847]/90">
                      Explore {brand.title}
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-[#0D2847] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Rudra Essence?</h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              We bring together the world's most innovative wellness technologies and scientifically proven solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Beaker className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Science-Backed</h3>
              <p className="text-white/70">
                Every product we represent is supported by rigorous scientific research and proven clinical results.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Premium Quality</h3>
              <p className="text-white/70">
                We partner with industry-leading brands known for their commitment to excellence and innovation.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Proven Results</h3>
              <p className="text-white/70">
                Our wellness solutions deliver tangible benefits backed by real user testimonials and documented outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0D2847] mb-4">
            Ready to Transform Your Wellness?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Connect with us today to learn more about our premium wellness solutions and find the perfect fit for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#0D2847] hover:bg-[#0D2847]/90">
                Contact Us
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/blog">
              <Button size="lg" variant="outline" className="border-[#0D2847] text-[#0D2847] hover:bg-[#0D2847]/5">
                Read Our Blog
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
